import { axiosInstance } from '@/shared/lib/axios';
import type {
  Invitation,
  InvitationCreateInput,
  StaffMembership,
  StaffMembershipStatusUpdate,
  StaffMembershipUpdateInput,
  StaffMembershipResponse,
  StaffServiceSettingsResponse,
  StaffServiceSettingUpdateInput,
} from '@/shared/store/types';

const BASE_URL = '/api/v1';

/**
 * Canonicalize the contractType field on outbound membership writes.
 *
 * The shared `ContractType` enum still carries the legacy `BOOTH_RENTAL`
 * value (used in display code), but the backend Prisma enum is `BOOTH_RENT`.
 * Writes must always send the canonical form so the API never has to sniff
 * which alias arrived. Read responses already use the canonical form, so
 * this is a write-side-only normalization.
 */
const canonicalizeContractType = <T extends { contractType?: unknown }>(
  payload: T,
): T => {
  if (payload.contractType === 'BOOTH_RENTAL') {
    return { ...payload, contractType: 'BOOTH_RENT' };
  }
  return payload;
};

// Booth Rental Tier Types
export interface BoothRentalTier {
  id: string;
  membershipId: string;
  threshold: number;
  feeAmount: number;
  feeType: 'FLAT_RATE' | 'PERCENTAGE';
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BoothRentalTierCreateInput {
  membershipId: string;
  threshold: number;
  feeAmount: number;
  feeType: 'FLAT_RATE' | 'PERCENTAGE';
}

export interface BoothRentalTierUpdateInput {
  threshold?: number;
  feeAmount?: number;
  feeType?: 'FLAT_RATE' | 'PERCENTAGE';
  isActive?: boolean;
}

export const staff = {
  getMembership: async (id: string): Promise<StaffMembership> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/staff-memberships/${id}`,
    );
    return response.data;
  },

  updateMembershipStatus: async (
    id: string,
    data: StaffMembershipStatusUpdate,
  ): Promise<StaffMembership> => {
    const response = await axiosInstance.patch(
      `${BASE_URL}/staff-memberships/${id}/status`,
      data,
    );
    return response.data;
  },

  updateMembership: async (
    id: string,
    data: StaffMembershipUpdateInput,
  ): Promise<StaffMembership> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/staff-memberships/${id}`,
      canonicalizeContractType(data),
    );
    return response.data;
  },

  getServiceSettings: async (
    membershipId: string,
  ): Promise<StaffServiceSettingsResponse> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/staff-memberships/${membershipId}/service-settings`,
    );
    return response.data;
  },

  updateServiceSetting: async (
    membershipId: string,
    serviceId: string,
    data: StaffServiceSettingUpdateInput,
  ): Promise<StaffServiceSettingsResponse> => {
    const response = await axiosInstance.patch(
      `${BASE_URL}/staff-memberships/${membershipId}/service-settings/${serviceId}`,
      data,
    );
    return response.data;
  },

  // Shop Staff Operations
  getShopStaff: async (
    shopId: string,
    params?: {
      isActive?: boolean;
      contractType?: string;
      startDate?: string;
      endDate?: string;
      include?: string[];
    },
  ): Promise<StaffMembershipResponse[]> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/staff-memberships/shop/${shopId}/staff`,
      { params },
    );
    return response.data;
  },

  // Provider Memberships
  getProviderMemberships: async (
    providerId: string,
    params?: {
      isActive?: boolean;
      include?: string[];
      startDate?: string;
      endDate?: string;
    },
  ): Promise<StaffMembershipResponse[]> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/staff-memberships/provider/${providerId}`,
      { params },
    );
    return response.data;
  },

  // Staff Invitation Operations
  createStaffInvitation: async (
    shopId: string,
    data: InvitationCreateInput,
  ): Promise<Invitation> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/shops/${shopId}/invitations`,
      data,
    );
    return response.data;
  },

  getInvitation: async (code: string): Promise<Invitation> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/shops/invitations/${code}`,
    );
    return response.data;
  },

  validateInvitation: async (
    code: string,
  ): Promise<{
    isValid: boolean;
    reason?: 'expired' | 'used' | 'not_found' | 'invalid';
  }> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/shops/invitations/${code}/validate`,
    );
    return response.data;
  },

  resendInvitation: async (id: string): Promise<Invitation> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/shops/invitations/${id}/resend`,
    );
    return response.data;
  },

  getPendingInvitations: async (shopId: string): Promise<Invitation[]> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/shops/${shopId}/invitations`,
    );
    return response.data;
  },

  // Booth Rental Tier Operations — all routes live under /booth-rentals
  createRentalTier: async (
    membershipId: string,
    data: BoothRentalTierCreateInput,
  ): Promise<BoothRentalTier> => {
    const response = await axiosInstance.post(
      `${BASE_URL}/booth-rentals/membership/${membershipId}/tiers`,
      data,
    );
    return response.data;
  },

  getRentalTier: async (id: string): Promise<BoothRentalTier> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/booth-rentals/tiers/${id}`,
    );
    return response.data;
  },

  updateRentalTier: async (
    id: string,
    data: BoothRentalTierUpdateInput,
  ): Promise<BoothRentalTier> => {
    const response = await axiosInstance.put(
      `${BASE_URL}/booth-rentals/tiers/${id}`,
      data,
    );
    return response.data;
  },

  getMembershipRentalTiers: async (
    membershipId: string,
    isActive?: boolean,
  ): Promise<BoothRentalTier[]> => {
    const response = await axiosInstance.get(
      `${BASE_URL}/booth-rentals/membership/${membershipId}/tiers`,
      { params: isActive !== undefined ? { isActive } : {} },
    );
    return response.data;
  },

  deleteRentalTier: async (id: string): Promise<void> => {
    await axiosInstance.delete(
      `${BASE_URL}/booth-rentals/tiers/${id}`,
    );
  },

  /**
   * Invite an existing same-owner staff member to also join another
   * shop the caller owns. Sends a consent-based invitation; the staff
   * member must accept before a membership is created.
   */
  inviteToAnotherShop: async (data: {
    targetShopId: string;
    providerId: string;
    contractType: 'COMMISSION' | 'BOOTH_RENT';
    commissionRate?: number;
    isBookable?: boolean;
    canManageServices?: boolean;
  }): Promise<unknown> => {
    const { targetShopId, ...body } = data;
    const response = await axiosInstance.post(
      `${BASE_URL}/shops/${targetShopId}/invitations/from-existing-staff`,
      body,
    );
    return response.data;
  },
};
